import { useState, useEffect, useCallback } from 'react';
import { bookings as bookingsApi, guests as guestsApi, hotelogram as hotelogramApi, nftLifecycle as nftApi, flashBounties as bountiesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Hotel, Search, CalendarDays, MapPin, CreditCard, ShieldCheck,
  ArrowLeft, ShoppingBag, Tag, Star, Users, FileText, Link2, Copy,
  Share2, TrendingUp, Award, Coins, Image, ThumbsUp, ExternalLink,
  Flame, Timer, Target, Wallet, LogOut
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ReceiptViewer } from '@/components/ReceiptViewer';
import { GuestVerificationCard } from '@/components/verification/GuestVerificationCard';
import SbtVerifyBanner from '@/components/verification/SbtVerifyBanner';
import ReputationTimeline from '@/components/reputation/ReputationTimeline';
import FamilyCard from '@/components/family/FamilyCard';
import TrustedBadgeShareCard from '@/components/reputation/TrustedBadgeShareCard';
import { ReliabilityScoreCard } from '@/components/reliability/ReliabilityScoreCard';
import { RefundScenarios } from '@/components/cancellation/RefundScenarios';
import { useAppKitAccount, useDisconnect } from '@reown/appkit/react';
import { BACKEND_URL } from '@/lib/backendUrl';

const TABS = ['stays', 'referrals', 'bounties', 'share', 'content', 'earnings', 'nfts'];
const TAB_LABELS = {
  stays: 'My Stays',
  referrals: 'Referral Links',
  bounties: 'Bounty Board',
  share: 'Share Center',
  content: 'Content',
  earnings: 'Earnings',
  nfts: 'NFT Gallery',
};
const TAB_ICONS = {
  stays: Hotel,
  referrals: Link2,
  bounties: Flame,
  share: Share2,
  content: Star,
  earnings: Coins,
  nfts: Image,
};

const TIER_COLORS = {
  Explorer: 'bg-slate-100 text-slate-700 border-slate-300',
  Creator: 'bg-blue-100 text-blue-700 border-blue-300',
  Ambassador: 'bg-amber-100 text-amber-700 border-amber-300',
  Legend: 'bg-purple-100 text-purple-800 border-purple-400',
};

function CreatorScoreBadge({ score }) {
  if (!score) return null;
  const pct = Math.min(score.total_score / 1000 * 100, 100);
  const tierColor = TIER_COLORS[score.tier] || TIER_COLORS.Explorer;

  return (
    <Card className="border-primary/20" data-testid="creator-score-badge">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h3 className="font-semibold heading-font text-sm">Creator Score</h3>
          </div>
          <Badge className={`${tierColor} border text-xs font-semibold`} data-testid="creator-tier-badge">
            {score.tier}
          </Badge>
        </div>
        <div className="flex items-end gap-3 mb-3">
          <span className="text-3xl font-bold mono" data-testid="creator-score-value">{score.total_score}</span>
          <span className="text-sm text-muted-foreground mb-1">/ 1000</span>
          <span className="text-xs text-muted-foreground mb-1 ml-auto">{score.commission_pct}% commission</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        {score.next_tier && (
          <p className="text-xs text-muted-foreground mt-2">
            {score.next_tier.min_score - score.total_score} pts to <strong>{score.next_tier.name}</strong> ({score.next_tier.commission_pct}% commission)
          </p>
        )}
        <div className="grid grid-cols-4 gap-2 mt-3">
          {Object.entries(score.breakdown || {}).map(([key, val]) => (
            <div key={key} className="text-center">
              <p className="text-lg font-bold mono">{val.score}</p>
              <p className="text-[10px] text-muted-foreground">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ReferralCard({ referral, onCopy }) {
  const refUrl = `${window.location.origin}/book?ref=${referral.referral_code}`;
  const copyLink = () => {
    navigator.clipboard.writeText(refUrl);
    toast.success('Referral link copied!');
    onCopy?.();
  };

  return (
    <Card className="overflow-hidden" data-testid="referral-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold heading-font text-sm">{referral.hotel_id?.replace('hotel_', '').replace(/_/g, ' ')}</h4>
          <Badge variant={referral.is_active ? 'default' : 'secondary'} className="text-[10px]">
            {referral.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 mb-3">
          <code className="text-xs flex-1 truncate mono" data-testid="referral-link">{refUrl}</code>
          <Button variant="ghost" size="sm" onClick={copyLink} data-testid="copy-referral-link">
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-bold mono">{referral.clicks || 0}</p>
            <p className="text-[10px] text-muted-foreground">Clicks</p>
          </div>
          <div>
            <p className="text-lg font-bold mono">{referral.bookings || 0}</p>
            <p className="text-[10px] text-muted-foreground">Bookings</p>
          </div>
          <div>
            <p className="text-lg font-bold mono text-primary">{referral.total_commission_hermes || 0}</p>
            <p className="text-[10px] text-muted-foreground">$HERMES</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => {
            const text = encodeURIComponent(`I just stayed at this amazing hotel! Book through my link: ${refUrl}`);
            navigator.clipboard.writeText(`I just stayed at this amazing hotel! Book through my link: ${refUrl}`);
            toast.success('Share text copied! Paste on TikTok');
          }} data-testid="share-tiktok">
            <ExternalLink className="w-3 h-3 mr-1" /> TikTok
          </Button>
          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => {
            navigator.clipboard.writeText(`Check out this hotel and book through my link for rewards! ${refUrl}`);
            toast.success('Share text copied! Paste on Instagram');
          }} data-testid="share-instagram">
            <ExternalLink className="w-3 h-3 mr-1" /> Instagram
          </Button>
          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => {
            const text = encodeURIComponent(`Just discovered this hotel on @getbitsyai! Book with my link and earn $HERMES: ${refUrl}`);
            window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
          }} data-testid="share-twitter">
            <ExternalLink className="w-3 h-3 mr-1" /> X / Twitter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentCreateDialog({ hotelId, guestId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', rating: 5 });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.title || !form.body) { toast.error('Title and review are required'); return; }
    setLoading(true);
    try {
      await hotelogramApi.createContent({
        guest_id: guestId,
        hotel_id: hotelId,
        title: form.title,
        body: form.body,
        rating: form.rating,
      });
      toast.success('Content created! Your Creator Score will update.');
      setOpen(false);
      setForm({ title: '', body: '', rating: 5 });
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create content');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-xs" data-testid="create-content-button">
          <Star className="w-3 h-3 mr-1" /> Write Review
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="heading-font">Share Your Experience</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Amazing ocean views!" data-testid="content-title-input" />
          </div>
          <div>
            <Label>Your Review</Label>
            <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              placeholder="Share your honest experience..." rows={4} data-testid="content-body-input" />
          </div>
          <div>
            <Label>Rating</Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setForm({ ...form, rating: n })}
                  className={`p-1 ${form.rating >= n ? 'text-amber-500' : 'text-muted-foreground'}`}
                  data-testid={`rating-star-${n}`}>
                  <Star className="w-6 h-6" fill={form.rating >= n ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full" data-testid="submit-content-button">
            {loading ? 'Publishing...' : 'Publish Review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContentCard({ content }) {
  const [votes, setVotes] = useState(content.helpful_votes || 0);

  const voteHelpful = async () => {
    try {
      await hotelogramApi.voteHelpful(content.content_id);
      setVotes(v => v + 1);
      toast.success('Marked as helpful');
    } catch {
      toast.error('Already voted');
    }
  };

  return (
    <Card data-testid="content-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold heading-font text-sm">{content.title}</h4>
          <div className="flex items-center gap-1">
            {Array.from({ length: content.rating }, (_, i) => (
              <Star key={i} className="w-3 h-3 text-amber-500" fill="currentColor" />
            ))}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{content.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {content.is_verified_stay && (
              <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">
                <ShieldCheck className="w-3 h-3 mr-1" /> Verified Stay
              </Badge>
            )}
            <Badge variant="secondary" className="text-[10px]">Quality: {content.quality_score}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={voteHelpful} className="text-xs" data-testid="helpful-vote-button">
            <ThumbsUp className="w-3 h-3 mr-1" /> {votes}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NFTCard({ nft }) {
  const isCollectible = nft.phase === 'stay_collectible';
  return (
    <Card className={`overflow-hidden ${isCollectible ? 'border-primary/30' : ''}`} data-testid="nft-card">
      {isCollectible && nft.art_url && (
        <div className="h-40 bg-muted">
          <img src={nft.art_url} alt="Stay Art" className="w-full h-full object-cover" />
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold heading-font text-sm">{nft.hotel_name || nft.hotel_id}</h4>
          <Badge className={isCollectible ? 'bg-primary/10 text-primary border-primary/30' : ''} variant="outline">
            {isCollectible ? 'Collectible' : 'Active Booking'}
          </Badge>
        </div>
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {nft.check_in} - {nft.check_out}</p>
          <p className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> ${nft.original_price} on {nft.nft_chain}</p>
        </div>
        {nft.resale_history?.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            Resold {nft.resale_history.length} time{nft.resale_history.length > 1 ? 's' : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReceiptDialog({ bookingId }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-xs" data-testid="view-receipt-button">
          <FileText className="w-3 h-3 mr-1" /> Receipt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading-font">Booking Receipt</DialogTitle></DialogHeader>
        <ReceiptViewer bookingId={bookingId} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function ShareCardModal({ open, onOpenChange, cardData, loading }) {
  const downloadCard = () => {
    if (!cardData?.image_base64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${cardData.image_base64}`;
    link.download = `hotelogram-${cardData.hotel_name?.replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('Card downloaded!');
  };

  const shareToSocial = (platform) => {
    const text = encodeURIComponent(
      `Just stayed at ${cardData?.hotel_name}! Book with my link and earn $HERMES rewards too: ${cardData?.referral_url}`
    );
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?quote=${text}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank');
    else {
      navigator.clipboard.writeText(decodeURIComponent(text));
      toast.success('Share text copied! Paste it on ' + platform);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-card-modal">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" /> Share Your Stay
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="share-card-loading">
            <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Creating your unique share card with AI...</p>
            <p className="text-xs text-muted-foreground">This may take up to 30 seconds</p>
          </div>
        ) : cardData?.image_base64 ? (
          <div className="space-y-4">
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src={`data:image/png;base64,${cardData.image_base64}`}
                alt="Share Card"
                className="w-full"
                data-testid="share-card-image"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadCard} className="flex-1" data-testid="download-share-card">
                <ExternalLink className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button size="sm" variant="outline" onClick={() => shareToSocial('twitter')} data-testid="share-twitter">
                X / Twitter
              </Button>
              <Button size="sm" variant="outline" onClick={() => shareToSocial('instagram')} data-testid="share-instagram">
                Instagram
              </Button>
              <Button size="sm" variant="outline" onClick={() => shareToSocial('tiktok')} data-testid="share-tiktok">
                TikTok
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground">
              Download the card, then upload it to your favorite platform with the referral link!
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>Failed to generate share card. Please try again.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareButton({ bookingId, guestEmail }) {
  const [open, setOpen] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await hotelogramApi.generateShareCard({ guest_email: guestEmail, booking_id: bookingId });
      setCardData(res.data?.card || res.data);
    } catch (e) {
      toast.error('Failed to generate share card');
      setCardData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="text-xs" onClick={generate} data-testid="share-stay-button">
        <Share2 className="w-3 h-3 mr-1" /> Share Stay
      </Button>
      <ShareCardModal open={open} onOpenChange={setOpen} cardData={cardData} loading={loading} />
    </>
  );
}

export default function GuestPortalPage() {
  const [email, setEmail] = useState('');
  const [activeTab, setActiveTab] = useState('stays');
  const [results, setResults] = useState(null);
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [nftPortfolio, setNftPortfolio] = useState(null);
  const [shareCards, setShareCards] = useState([]);
  const [activeBounties, setActiveBounties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);

  const { address, isConnected } = useAppKitAccount();
  const { disconnect } = useDisconnect();
  const location = useLocation();
  const navigate = useNavigate();

  const lookup = useCallback(async (lookupEmail) => {
    const targetEmail = lookupEmail || email.trim();
    if (!targetEmail) return;
    setLoading(true);
    try {
      const [bookingsRes, profileRes, dashRes] = await Promise.all([
        bookingsApi.getByEmail(targetEmail),
        guestsApi.profile(targetEmail).catch(() => ({ data: null })),
        hotelogramApi.getDashboard(targetEmail).catch(() => ({ data: null })),
      ]);
      setResults(bookingsRes.data);
      setProfile(profileRes.data);
      setDashboard(dashRes.data);

      // Load NFT portfolio and share cards
      nftApi.getPortfolio(targetEmail).then(r => setNftPortfolio(r.data)).catch(() => {});
      hotelogramApi.getShareCards(targetEmail).then(r => setShareCards(r.data?.cards || [])).catch(() => {});

      // Auto-link wallet if connected
      if (isConnected && address) {
        const api = BACKEND_URL;
        fetch(`${api}/api/guest-wallet/link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: address, email: targetEmail }),
        }).catch(() => {});
      }
    } catch {
      setResults([]);
    } finally { setLoading(false); }
  }, [email, isConnected, address]);

  // Load bounties on mount (global, not per-email)
  useEffect(() => {
    bountiesApi.getBoard().then(r => setActiveBounties(r.data?.bounties || [])).catch(() => {});
  }, []);

  // Auto-load if wallet connected and guest has a linked email
  useEffect(() => {
    if (isConnected && address && !results) {
      // Check if there's a linked email for this wallet
      const api = BACKEND_URL;
      fetch(`${api}/api/guest-wallet/lookup?wallet=${address}`)
        .then(r => r.json())
        .then(data => {
          if (data?.email) {
            setEmail(data.email);
            lookup(data.email);
          }
        })
        .catch(() => {});
    }
  }, [isConnected, address, results, lookup]);

  const generateReferral = async (hotelId) => {
    try {
      await hotelogramApi.generateReferral({ guest_id: email, hotel_id: hotelId });
      // Refresh dashboard
      const r = await hotelogramApi.getDashboard(email);
      setDashboard(r.data);
      toast.success('Referral link generated!');
    } catch (e) {
      toast.error('Failed to generate referral link');
    }
  };

  const canResell = (b) => {
    return b.booking_status === 'confirmed' &&
      b.payment_status === 'confirmed' &&
      b.payment_method === 'crypto' &&
      b.nft_token_id !== null && b.nft_token_id !== undefined &&
      !b.marketplace_listed && !b.marketplace_transferred;
  };

  const stats = dashboard?.stats || {};
  const score = dashboard?.creator_score;

  return (
    <div className="lobby-theme min-h-screen" data-testid="hotelogram-page">
      {/* Header */}
      <nav className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <Share2 className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
              <span className="font-bold heading-font text-lg">Hotelogram</span>
              <span className="text-xs text-muted-foreground hidden sm:block">by getbitsy.ai</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Badge variant="outline" className="text-xs font-mono flex items-center gap-1" data-testid="wallet-badge">
                  <Wallet className="w-3 h-3" />
                  {address?.slice(0, 6)}...{address?.slice(-4)}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => { disconnect(); navigate('/guest-login'); }} data-testid="disconnect-wallet">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Link to="/guest-login">
                <Button variant="outline" size="sm" data-testid="connect-wallet-cta">
                  <Wallet className="w-4 h-4 mr-1" /> Connect
                </Button>
              </Link>
            )}
            <Link to="/marketplace"><Button variant="ghost" size="sm"><ShoppingBag className="w-4 h-4 mr-1" /> Marketplace</Button></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold heading-font mb-1">Your Travel. Your Earnings.</h1>
          <p className="text-muted-foreground text-sm">Share hotels you love, earn $HERMES on every booking</p>
        </div>

        <div className="flex gap-2 max-w-md mx-auto mb-8">
          <Input value={email} onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && lookup()}
            placeholder={isConnected ? 'Loading your profile...' : 'your@email.com'} className="h-11"
            data-testid="hotelogram-email-input" />
          <Button onClick={() => lookup()} disabled={loading || !email.trim()} className="h-11 px-6" data-testid="hotelogram-lookup-button">
            <Search className="w-4 h-4 mr-1" /> {loading ? 'Loading...' : 'Go'}
          </Button>
        </div>

        {results !== null && (
          <div data-testid="hotelogram-dashboard">
            {/* Stats Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Card><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold mono">{stats.total_stays || 0}</p>
                <p className="text-[10px] text-muted-foreground">Verified Stays</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold mono">{stats.total_referrals || 0}</p>
                <p className="text-[10px] text-muted-foreground">Referral Bookings</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold mono">{stats.total_content || 0}</p>
                <p className="text-[10px] text-muted-foreground">Reviews</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold mono text-primary">{dashboard?.total_earned_hermes || 0}</p>
                <p className="text-[10px] text-muted-foreground">$HERMES Earned</p>
              </CardContent></Card>
            </div>

            {/* Creator Score */}
            <div className="mb-6">
              <CreatorScoreBadge score={score} />
            </div>

            {/* Verification & Reliability */}
            {email && (
              <>
                <SbtVerifyBanner email={email} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <GuestVerificationCard userEmail={email} />
                  <ReliabilityScoreCard userEmail={email} />
                </div>
                <div className="mb-6">
                  <ReputationTimeline email={email} />
                </div>
                {address && (
                  <div className="mb-6">
                    <TrustedBadgeShareCard wallet={address} />
                  </div>
                )}
                {address && (
                  <div className="mb-6">
                    <FamilyCard parentWallet={address} parentEmail={email} />
                  </div>
                )}
              </>
            )}

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto" data-testid="hotelogram-tabs">
              {TABS.map(tab => {
                const Icon = TAB_ICONS[tab];
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab}`}
                  >
                    <Icon className="w-4 h-4" />
                    {TAB_LABELS[tab]}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            {activeTab === 'stays' && (
              <div className="space-y-4" data-testid="tab-stays-content">
                {results.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No bookings found.</div>
                ) : (
                  results.map(({ booking: b, hotel, room }) => (
                    <Card key={b.id} className="overflow-hidden">
                      <CardContent className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold heading-font">{hotel?.name || 'Hotel'}</h3>
                              <Badge variant={b.booking_status === 'confirmed' ? 'default' : b.booking_status === 'cancelled' ? 'destructive' : 'secondary'}>
                                {b.booking_status}
                              </Badge>
                              {b.marketplace_listed && <Badge variant="outline" className="text-xs"><Tag className="w-3 h-3 mr-1" /> Listed</Badge>}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {hotel?.city}, {hotel?.country}</span>
                              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {b.check_in} - {b.check_out}</span>
                            </div>
                            {room && <p className="text-sm">Room: <span className="font-medium">{room.name}</span></p>}
                            {b.tx_hash && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                <span className="mono">{b.tx_hash.slice(0, 20)}...</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold mono">${b.final_price}</p>
                            <Badge variant="outline" className="text-xs mt-1">
                              <CreditCard className="w-3 h-3 mr-1" /> {b.payment_method}
                            </Badge>
                            <div className="mt-2 flex flex-col gap-2">
                              {b.booking_status === 'confirmed' && (
                                <Button size="sm" variant="outline" className="text-xs"
                                  onClick={() => setExpandedBooking(expandedBooking === b.id ? null : b.id)}
                                  data-testid="view-cancellation-policy-button">
                                  {expandedBooking === b.id ? 'Hide' : 'View'} Cancellation
                                </Button>
                              )}
                              {b.booking_status === 'confirmed' && (
                                <Button size="sm" variant="outline" className="text-xs"
                                  onClick={() => generateReferral(b.hotel_id)}
                                  data-testid="generate-referral-button">
                                  <Link2 className="w-3 h-3 mr-1" /> Get Referral Link
                                </Button>
                              )}
                              <ContentCreateDialog hotelId={b.hotel_id} guestId={email} onCreated={lookup} />
                              <ShareButton bookingId={b.id} guestEmail={email} />
                              {b.payment_status === 'confirmed' && <ReceiptDialog bookingId={b.id} />}
                            </div>
                          </div>
                        </div>
                        {expandedBooking === b.id && b.booking_status === 'confirmed' && (
                          <div className="mt-4 pt-4 border-t">
                            <RefundScenarios
                              booking={{
                                final_price: b.final_price,
                                locked_refund_pct: b.locked_refund_pct || 50,
                                check_in: b.check_in,
                                pricing_tier: b.pricing_tier || 'Standard'
                              }}
                              showCancelButton={true}
                              onCancelClick={() => toast.info('Cancellation flow coming soon')}
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {activeTab === 'referrals' && (
              <div data-testid="tab-referrals-content">
                {(dashboard?.referral_links || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Link2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No referral links yet</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      {results && results.length > 0
                        ? 'Go to My Stays and click "Get Referral Link" on any confirmed booking'
                        : 'Book a hotel first, then generate referral links to earn $HERMES on every booking through your link'}
                    </p>
                    {results && results.length > 0 ? (
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => setActiveTab('stays')} data-testid="referral-go-to-stays">
                        <Hotel className="w-3 h-3 mr-1" /> Go to My Stays
                      </Button>
                    ) : (
                      <Link to="/">
                        <Button variant="outline" size="sm" className="text-xs" data-testid="referral-browse-hotels">
                          <Hotel className="w-3 h-3 mr-1" /> Browse Hotels
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(dashboard?.referral_links || []).map(r => (
                      <ReferralCard key={r.referral_code} referral={r} />
                    ))}
                  </div>
                )}
              </div>
            )}


            {activeTab === 'bounties' && (
              <div data-testid="tab-bounties-content">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Flame className="w-5 h-5 text-amber-500" />
                      <h3 className="font-semibold heading-font">Bounty Board</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Hotels offering boosted $HERMES rewards right now
                    </p>
                  </div>
                  <Link to="/bounty-board">
                    <Button variant="outline" size="sm" className="text-xs" data-testid="view-full-bounty-board">
                      Full Board <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
                {activeBounties.length === 0 ? (
                  <div className="text-center py-12">
                    <Flame className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No active bounties right now</p>
                    <p className="text-xs text-muted-foreground">Check back soon — hotels post bounties to fill rooms fast</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeBounties.map(b => (
                      <Card key={b.id} className={`${b.token_multiplier >= 3 ? 'ring-1 ring-amber-400/40' : ''}`} data-testid="bounty-card-inline">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {b.token_multiplier >= 3 && <Flame className="w-4 h-4 text-amber-500" />}
                                <h4 className="font-semibold text-sm heading-font">{b.title}</h4>
                              </div>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Hotel className="w-3 h-3" /> {b.hotel_name}
                                {b.hotel_city && <span>| {b.hotel_city}</span>}
                              </p>
                              {b.description && <p className="text-xs text-muted-foreground mt-1">{b.description}</p>}
                            </div>
                            <div className="flex flex-col items-end gap-1 ml-3">
                              {b.token_multiplier > 1 && (
                                <Badge className="bg-amber-500/15 text-amber-600 border-amber-300 font-bold text-xs">
                                  {b.token_multiplier}x
                                </Badge>
                              )}
                              {b.commission_override_pct > 0 && (
                                <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-300 text-xs">
                                  {b.commission_override_pct}%
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                            <span className="flex items-center gap-0.5">
                              <Timer className="w-3 h-3" />
                              {b.expires_at ? (() => {
                                const diff = new Date(b.expires_at) - new Date();
                                if (diff <= 0) return 'Expired';
                                const hrs = Math.floor(diff / 3600000);
                                const mins = Math.floor((diff % 3600000) / 60000);
                                if (hrs > 24) return `${Math.floor(hrs / 24)}d left`;
                                return hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
                              })() : 'No expiry'}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <Target className="w-3 h-3" /> {b.bookings_generated || 0} bookings
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'share' && (
              <div data-testid="tab-share-content">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Share2 className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold heading-font">Share Center</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Generate AI-powered visual cards for your stays and share them on social media with your referral link
                  </p>
                </div>
                {results && results.length > 0 ? (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold heading-font">Your Stays</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.map(({ booking: b, hotel }) => {
                        const existingCard = shareCards.find(c => c.booking_id === b.id);
                        return (
                          <Card key={b.id} data-testid="share-center-booking-card">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold heading-font text-sm">{hotel?.name || 'Hotel'}</h4>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <CalendarDays className="w-3 h-3" /> {b.check_in} - {b.check_out}
                                  </p>
                                </div>
                                <ShareButton bookingId={b.id} guestEmail={email} />
                              </div>
                              {existingCard?.image_base64 && (
                                <div className="rounded-lg overflow-hidden border mt-2">
                                  <img
                                    src={`data:image/png;base64,${existingCard.image_base64}`}
                                    alt="Share Card"
                                    className="w-full"
                                    data-testid="existing-share-card-preview"
                                  />
                                </div>
                              )}
                              {!existingCard && (
                                <div className="text-center py-6 bg-muted/30 rounded-lg mt-2">
                                  <Share2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                  <p className="text-xs text-muted-foreground">Click "Share Stay" to generate your unique AI card</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Share2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No stays to share yet</p>
                    <p className="text-xs text-muted-foreground mb-4">Book a hotel first, then come back here to create shareable cards with your referral link</p>
                    <Link to="/">
                      <Button variant="outline" size="sm" className="text-xs" data-testid="share-center-book-cta">
                        <Hotel className="w-3 h-3 mr-1" /> Browse Hotels
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'content' && (
              <div className="space-y-4" data-testid="tab-content-content">
                {(dashboard?.content || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No reviews yet</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      {results && results.length > 0
                        ? 'Go to My Stays and click "Write Review" on any hotel you\'ve stayed at'
                        : 'Book and complete a stay first, then share your experience to boost your Creator Score'}
                    </p>
                    {results && results.length > 0 ? (
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => setActiveTab('stays')} data-testid="content-go-to-stays">
                        <Hotel className="w-3 h-3 mr-1" /> Go to My Stays
                      </Button>
                    ) : (
                      <Link to="/">
                        <Button variant="outline" size="sm" className="text-xs" data-testid="content-browse-hotels">
                          <Hotel className="w-3 h-3 mr-1" /> Browse Hotels
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  (dashboard?.content || []).map(c => <ContentCard key={c.content_id} content={c} />)
                )}
              </div>
            )}

            {activeTab === 'earnings' && (
              <div data-testid="tab-earnings-content">
                <Card className="mb-4">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold heading-font">Earnings</h3>
                    </div>
                    <p className="text-4xl font-bold mono text-primary" data-testid="total-earnings">
                      {dashboard?.total_earned_hermes || 0} <span className="text-lg text-muted-foreground">$HERMES earned</span>
                    </p>
                    {(dashboard?.total_pending_hermes || 0) > 0 && (
                      <p className="text-sm text-amber-600 mt-1" data-testid="pending-earnings">
                        +{dashboard.total_pending_hermes} $HERMES pending (released after guest checkout)
                      </p>
                    )}
                  </CardContent>
                </Card>
                {(dashboard?.commissions || []).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No commissions yet. Share your referral links to start earning!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold heading-font">Commission History</h4>
                    {(dashboard?.commissions || []).map((c, i) => (
                      <Card key={i}>
                        <CardContent className="p-3 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{c.hotel_id?.replace('hotel_', '').replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">Booking ${c.booking_amount} at {c.commission_pct}%</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold mono ${c.status === 'confirmed' ? 'text-primary' : c.status === 'cancelled' ? 'text-muted-foreground line-through' : 'text-amber-600'}`}>
                              {c.status === 'cancelled' ? '' : '+'}{c.commission_hermes} $HERMES
                            </p>
                            <div className="flex items-center gap-1 justify-end">
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${c.status === 'confirmed' ? 'text-emerald-600 border-emerald-300' : c.status === 'cancelled' ? 'text-red-500 border-red-300' : 'text-amber-600 border-amber-300'}`}
                                data-testid={`commission-status-${c.status}`}
                              >
                                {c.status === 'confirmed' ? 'Paid' : c.status === 'cancelled' ? 'Cancelled' : 'Pending checkout'}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">{c.creator_tier}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'nfts' && (
              <div data-testid="tab-nfts-content">
                {!nftPortfolio || nftPortfolio.total === 0 ? (
                  <div className="text-center py-12">
                    <Image className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-2">No NFTs yet</p>
                    <p className="text-xs text-muted-foreground">Book with crypto to mint your first Booking NFT</p>
                  </div>
                ) : (
                  <>
                    {nftPortfolio.active_bookings?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold heading-font mb-3">Active Booking NFTs</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {nftPortfolio.active_bookings.map(n => <NFTCard key={n.booking_id} nft={n} />)}
                        </div>
                      </div>
                    )}
                    {nftPortfolio.collectibles?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold heading-font mb-3">Stay Collectibles</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {nftPortfolio.collectibles.map(n => <NFTCard key={n.booking_id} nft={n} />)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
